import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../AppIcon';
import { useAuth } from '../../context/AuthContext';
import {
  fetchTicket,
  fetchTicketMessages,
  submitTicketRating,
  postTicketMessage,
  uploadSupportImage,
  notifySuperAdminsFollowUp,
  buildSupportTicketThread,
  formatTicketNumber,
  wasTicketReopened,
  subscribeToSupportTicket,
  subscribeToSupportTicketMessages,
} from '../../services/supportTicketService';
import { compressSupportImage } from '../../utils/compressSupportImage';

const cleanTitle = (t) => t?.replace(/^\[(bug|suggestion|question|other)\]\s*/i, '') ?? t;

const fmtTime = (d) =>
  d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const COMPOSE_SHELL = 'rounded-xl bg-slate-50/90 px-3 py-2 ring-1 ring-inset ring-slate-200/60 transition-all focus-within:bg-white focus-within:ring-violet-200/80 focus-within:shadow-sm';
const COMPOSE_TEXTAREA = 'w-full resize-none border-0 bg-transparent p-1 text-sm leading-relaxed text-slate-800 shadow-none outline-none ring-0 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0';

const StarRating = ({ value, onChange, readOnly }) => (
  <div className="flex justify-center gap-1.5 sm:gap-2">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={readOnly}
        onClick={() => !readOnly && onChange?.(star)}
        className={`text-2xl sm:text-3xl transition-transform ${readOnly ? 'cursor-default' : 'hover:scale-110 active:scale-95'} ${
          star <= value ? 'text-amber-400' : 'text-slate-200'
        }`}
      >
        ★
      </button>
    ))}
  </div>
);

function NewMessagePill({ visible, label, onJump }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          initial={{ opacity: 0, y: 12, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ type: 'spring', damping: 22, stiffness: 320 }}
          onClick={onJump}
          className="sticky bottom-2 z-20 mx-auto mt-3 flex w-fit items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-lg shadow-blue-600/30"
        >
          <motion.span
            animate={{ y: [0, 3, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Icon name="ChevronDown" size={14} />
          </motion.span>
          {label}
        </motion.button>
      )}
    </AnimatePresence>
  );
}

function ScrollHintStrip({ visible }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="shrink-0 overflow-hidden border-t border-slate-100 bg-slate-50/90"
        >
          <motion.div
            animate={{ y: [0, 3, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            className="flex items-center justify-center gap-2 px-4 py-2.5"
          >
            <Icon name="ChevronDown" size={15} className="text-emerald-500" />
            <span className="text-xs font-medium text-slate-500">Scroll for more</span>
            <Icon name="ChevronDown" size={15} className="text-emerald-500" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ChatBubble({ isSupport, name, time, children, variant }) {
  const isResolution = variant === 'resolution';
  const isPastResolution = variant === 'past-resolution';

  return (
    <div className={`flex ${isSupport ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[88%] sm:max-w-[78%] ${isSupport ? 'items-start' : 'items-end'} flex flex-col`}>
        <div className={`mb-1 flex items-center gap-2 px-1 ${isSupport ? '' : 'flex-row-reverse'}`}>
          <span className="text-[11px] font-semibold text-slate-600">{name}</span>
          {time && <span className="text-[10px] text-slate-400">{time}</span>}
        </div>
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
            isResolution
              ? 'rounded-bl-md border border-emerald-200 bg-emerald-50 text-emerald-900'
              : isPastResolution
                ? 'rounded-bl-md border border-dashed border-slate-300 bg-slate-50 text-slate-700'
                : isSupport
                  ? 'rounded-bl-md border border-slate-200 bg-white text-slate-800'
                  : 'rounded-br-md bg-slate-800 text-white'
          }`}
        >
          {isResolution && (
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
              <Icon name="CheckCircle2" size={10} /> Resolved
            </span>
          )}
          {isPastResolution && (
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-slate-200/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
              <Icon name="History" size={10} /> Resolved earlier
            </span>
          )}
          <p className="whitespace-pre-wrap">{children}</p>
        </div>
      </div>
    </div>
  );
}

const HEADER = {
  resolved: {
    icon: 'CheckCircle2',
    badge: 'Resolved',
    title: 'Your issue has been solved',
    subtitle: 'Support marked your ticket as complete',
    gradient: 'from-emerald-600 to-teal-600',
  },
  reply: {
    icon: 'MessageSquare',
    badge: 'New reply',
    title: 'Support replied to your ticket',
    subtitle: 'Read the message below and reply if needed',
    gradient: 'from-blue-600 to-indigo-600',
  },
  open: {
    icon: 'Headphones',
    badge: 'Support ticket',
    title: 'Your support ticket',
    subtitle: 'View updates from our support team',
    gradient: 'from-slate-700 to-slate-900',
  },
};

export default function TicketResolvedModal({ ticketId, onClose }) {
  const { user, displayName } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showScrollHint, setShowScrollHint] = useState(false);

  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSaved, setRatingSaved] = useState(false);
  const [savingRating, setSavingRating] = useState(false);

  const [followUpText, setFollowUpText] = useState('');
  const [followUpImages, setFollowUpImages] = useState([]);
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [followUpSent, setFollowUpSent] = useState(false);
  const [followUpError, setFollowUpError] = useState('');
  const fileRef = useRef();
  const scrollRef = useRef(null);
  const conversationEndRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const [newMessageHint, setNewMessageHint] = useState(null);
  const newMessageTimerRef = useRef(null);

  const scrollToConversationBottom = useCallback((behavior = 'smooth') => {
    const end = conversationEndRef.current;
    if (end) {
      end.scrollIntoView({ behavior, block: 'end' });
      return;
    }
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const showNewMessageHint = useCallback((senderName) => {
    clearTimeout(newMessageTimerRef.current);
    setNewMessageHint(senderName || 'Support');
    newMessageTimerRef.current = setTimeout(() => setNewMessageHint(null), 7000);
  }, []);

  const isClosed = ticket?.status === 'closed';
  const isResolved = ticket?.status === 'resolved' || isClosed;
  const showReply = !followUpSent && !isClosed;
  const reopened = wasTicketReopened(ticket);
  const headerKey = isResolved ? 'resolved' : (messages.some((m) => m.sender_role === 'super_admin') ? 'reply' : 'open');
  const header = HEADER[headerKey];
  const thread = useMemo(() => buildSupportTicketThread(ticket, messages), [ticket, messages]);

  const updateScrollHint = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || error) {
      setShowScrollHint(false);
      return;
    }
    const hasOverflow = el.scrollHeight > el.clientHeight + 4;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    isNearBottomRef.current = nearBottom;
    setShowScrollHint(hasOverflow && !nearBottom);
  }, [loading, error]);

  const loadTicket = useCallback(() => {
    if (!ticketId) return Promise.resolve();
    return Promise.all([fetchTicket(ticketId), fetchTicketMessages(ticketId)])
      .then(([t, msgs]) => {
        if (!t) throw new Error('Ticket not found');
        setTicket(t);
        setMessages(msgs);
        if (t.rating) {
          setRating(t.rating);
          setRatingComment(t.rating_comment || '');
          setRatingSaved(true);
        }
      });
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    setError('');
    loadTicket()
      .catch((err) => setError(err.message || 'Could not load ticket details'))
      .finally(() => {
        setLoading(false);
        requestAnimationFrame(() => scrollToConversationBottom('auto'));
      });
  }, [ticketId, loadTicket, scrollToConversationBottom]);

  useEffect(() => {
    if (loading || error) return;
    const prev = prevMessageCountRef.current;
    const count = messages.length;
    if (count > prev && count > 0) {
      const last = messages[count - 1];
      if (last?.sender_role === 'super_admin') {
        if (isNearBottomRef.current) {
          requestAnimationFrame(() => scrollToConversationBottom('smooth'));
        } else {
          showNewMessageHint(last.sender_name || 'Support');
        }
      }
    }
    prevMessageCountRef.current = count;
  }, [messages, loading, error, scrollToConversationBottom, showNewMessageHint]);

  useEffect(() => {
    if (!ticketId || loading) return undefined;

    const syncMessages = () => fetchTicketMessages(ticketId).then(setMessages);
    const syncTicket = () => fetchTicket(ticketId).then((t) => {
      setTicket(t);
      if (t?.rating) {
        setRating(t.rating);
        setRatingComment(t.rating_comment || '');
        setRatingSaved(true);
      }
    });

    const msgSub = subscribeToSupportTicketMessages(ticketId, (payload) => {
      if (payload.eventType === 'INSERT' && payload.new) {
        const msg = payload.new;
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at),
          );
        });
      } else {
        syncMessages();
      }
    });

    const ticketSub = subscribeToSupportTicket(ticketId, () => {
      syncTicket();
    });

    return () => {
      msgSub?.unsubscribe();
      ticketSub?.unsubscribe();
    };
  }, [ticketId, loading]);

  useEffect(() => {
    if (loading) return;
    const t = requestAnimationFrame(updateScrollHint);
    const onResize = () => updateScrollHint();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(t);
      window.removeEventListener('resize', onResize);
    };
  }, [loading, error, ticket, messages, ratingSaved, followUpSent, thread, updateScrollHint]);

  const handleSaveRating = async () => {
    if (!rating) return;
    setSavingRating(true);
    try {
      await submitTicketRating({ ticketId, rating, comment: ratingComment });
      setRatingSaved(true);
    } finally {
      setSavingRating(false);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const compressed = await Promise.all(
      files.slice(0, 3).map((f) => compressSupportImage(f).catch(() => f)),
    );
    setFollowUpImages((prev) => [
      ...prev,
      ...compressed.map((f) => ({ file: f, preview: URL.createObjectURL(f) })),
    ].slice(0, 3));
  };

  const handleFollowUp = async () => {
    const body = followUpText.trim();
    if (!body) return;
    setSendingFollowUp(true);
    setFollowUpError('');
    try {
      const urls = [];
      for (const img of followUpImages) {
        try { urls.push(await uploadSupportImage(img.file, ticketId)); } catch { /* skip */ }
      }
      await postTicketMessage({
        ticketId,
        senderId: user?.id,
        senderName: displayName || user?.email,
        senderRole: 'user',
        body,
        imageUrls: urls,
      });
      await notifySuperAdminsFollowUp({ ticketId });
      setFollowUpSent(true);
      setFollowUpText('');
      setFollowUpImages([]);
      await loadTicket();
      requestAnimationFrame(() => scrollToConversationBottom('smooth'));
    } catch (err) {
      setFollowUpError(err?.message || 'Could not send message. Please try again.');
    } finally {
      setSendingFollowUp(false);
    }
  };

  const handleFollowUpKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sendingFollowUp && followUpText.trim()) handleFollowUp();
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {ticketId && (
        <div className="fixed inset-0 z-[26000] flex items-center justify-center p-4 sm:p-6">
          <motion.button
            type="button"
            key="overlay"
            aria-label="Close"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            key="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ticket-resolved-title"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: 'spring', damping: 28, stiffness: 340 }}
            className="relative z-10 flex w-full max-w-2xl max-h-[min(90dvh,780px)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`relative shrink-0 bg-gradient-to-r ${header.gradient} px-5 py-4 sm:px-6 sm:py-5`}>
              <div className="flex items-start gap-3 pr-10">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
                  <Icon name={header.icon} size={22} className="text-white" />
                </div>
                <div className="min-w-0 pt-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">{header.badge}</p>
                  <h2 id="ticket-resolved-title" className="text-lg font-bold text-white sm:text-xl leading-tight">
                    {header.title}
                  </h2>
                  <p className="mt-0.5 text-sm text-white/85">{header.subtitle}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors sm:right-4 sm:top-4"
                aria-label="Close"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            <div
              ref={scrollRef}
              onScroll={updateScrollHint}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50/80 px-5 py-5 sm:px-6 sm:py-6 [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent]"
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-14">
                  <Icon name="Loader2" size={28} className="animate-spin text-emerald-500" />
                  <p className="text-sm text-slate-500">Loading details…</p>
                </div>
              ) : error ? (
                <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-8 text-center">
                  <Icon name="AlertCircle" size={28} className="mx-auto mb-2 text-rose-400" />
                  <p className="text-sm font-semibold text-rose-700">{error}</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Subject</p>
                      <span className="text-[11px] font-mono font-semibold text-violet-600">{formatTicketNumber(ticket?.id)}</span>
                    </div>
                    <p className="mt-0.5 font-semibold text-slate-900">{cleanTitle(ticket?.title)}</p>
                  </div>

                  <section className="relative">
                    {reopened && (
                      <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                        <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0 text-amber-600" />
                        <p className="text-sm text-amber-900 leading-relaxed">
                          <span className="font-semibold">This ticket was reopened.</span>
                          {' '}Messages are shown in time order — older resolution notes may appear above newer replies.
                        </p>
                      </div>
                    )}
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conversation</h3>
                      <span className="text-[10px] text-slate-400">{thread.length} message{thread.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      {thread.length === 0 ? (
                        <p className="text-center text-sm text-slate-400 py-4">No messages yet.</p>
                      ) : (
                        thread.map((item) => (
                          <ChatBubble
                            key={item.id}
                            isSupport={item.isSupport}
                            name={item.name}
                            time={fmtTime(item.time)}
                            variant={item.variant}
                          >
                            {item.body}
                          </ChatBubble>
                        ))
                      )}
                      <div ref={conversationEndRef} className="h-1" />
                    </div>
                    <NewMessagePill
                      visible={!!newMessageHint}
                      label={`New message from ${newMessageHint}`}
                      onJump={() => {
                        scrollToConversationBottom('smooth');
                        setNewMessageHint(null);
                      }}
                    />
                  </section>

                  {isResolved && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3">
                      <Icon name="RefreshCw" size={15} className="mt-0.5 shrink-0 text-amber-600" />
                      <p className="text-sm text-amber-900 leading-relaxed">
                        <span className="font-medium">To see fixes,</span> refresh the page or reopen the app.
                      </p>
                    </div>
                  )}

                  {showReply ? (
                    <section>
                      <p className="mb-2 text-sm font-semibold text-slate-800">
                        {isResolved ? 'Still having the issue?' : 'Reply to support'}
                      </p>
                      <p className="mb-3 text-xs text-slate-500">
                        {isResolved ? "Let us know and we'll take another look." : 'Send a message to our support team.'}
                      </p>
                      {followUpError && (
                        <p className="mb-2 flex items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200/80">
                          <Icon name="AlertCircle" size={13} />
                          {followUpError}
                        </p>
                      )}
                      <div className={COMPOSE_SHELL}>
                        <textarea
                          value={followUpText}
                          onChange={(e) => { setFollowUpText(e.target.value); if (followUpError) setFollowUpError(''); }}
                          onKeyDown={handleFollowUpKeyDown}
                          placeholder="Type your message…"
                          rows={3}
                          className={COMPOSE_TEXTAREA}
                        />
                        <div className="mt-2 flex flex-col gap-2 border-t border-slate-200/60 pt-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                            <button
                              type="button"
                              onClick={() => fileRef.current?.click()}
                              className="flex items-center justify-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200/80 hover:bg-slate-50"
                            >
                              <Icon name="ImagePlus" size={13} /> Add screenshot
                            </button>
                            <span className="text-[10px] text-slate-400">Enter to send · Shift+Enter for new line</span>
                          </div>
                          <button
                            type="button"
                            disabled={!followUpText.trim() || sendingFollowUp}
                            onClick={handleFollowUp}
                            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:from-violet-700 hover:to-purple-700 disabled:opacity-40"
                          >
                            {sendingFollowUp ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Send" size={15} />}
                            Send message
                          </button>
                        </div>
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
                    </section>
                  ) : !isClosed && followUpSent ? (
                    <div className="rounded-xl bg-emerald-50 p-4 text-center ring-1 ring-inset ring-emerald-200/80">
                      <Icon name="CheckCircle2" size={22} className="text-emerald-500 mx-auto mb-1" />
                      <p className="text-sm font-semibold text-emerald-700">Message sent — we'll be in touch soon.</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <ScrollHintStrip visible={showScrollHint} />

            {isResolved && (
              <div className="shrink-0 border-t border-slate-200 bg-gradient-to-b from-amber-50/30 to-white px-5 py-4 sm:px-6">
                <p className="text-center text-sm font-semibold text-slate-800">How was our support?</p>
                <p className="text-center text-xs text-slate-400 mt-0.5 mb-3">
                  {isClosed ? 'This ticket is closed — please rate your experience' : 'Tap a star to rate'}
                </p>
                <StarRating value={rating} onChange={setRating} readOnly={ratingSaved} />
                {!ratingSaved && (
                  <>
                    <textarea
                      value={ratingComment}
                      onChange={(e) => setRatingComment(e.target.value)}
                      placeholder="Optional comment…"
                      rows={2}
                      className={`mt-3 ${COMPOSE_TEXTAREA} rounded-lg bg-slate-50/90 px-3 py-2 ring-1 ring-inset ring-slate-200/60 focus:bg-white focus:ring-violet-200/80`}
                    />
                    <button
                      type="button"
                      disabled={!rating || savingRating}
                      onClick={handleSaveRating}
                      className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40"
                    >
                      {savingRating ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Star" size={15} />}
                      Submit rating
                    </button>
                  </>
                )}
                {ratingSaved && (
                  <p className="mt-2.5 text-center text-xs font-semibold text-emerald-600">Thank you for your feedback!</p>
                )}
              </div>
            )}

            <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white hover:bg-slate-800 active:scale-[0.99] transition-all"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
